import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudioWrapsPage = () => {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [wraps, setWraps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFinish, setFilterFinish] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studioRes, wrapsRes] = await Promise.all([
          axios.get(`${API}/studios/public/${slug}`),
          axios.get(`${API}/wraps/public/${slug}`)
        ]);
        setStudio(studioRes.data);
        setWraps(wrapsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Studio Not Found</h1>
          <Link to="/" className="text-[#F5D061] hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const accentColor = studio.accent_color || '#FFFFFF';

  const filteredWraps = wraps.filter(wrap => {
    const matchesSearch = wrap.name.toLowerCase().includes(search.toLowerCase()) ||
                          wrap.category?.toLowerCase().includes(search.toLowerCase());
    const matchesFinish = filterFinish === 'all' || wrap.finish === filterFinish;
    return matchesSearch && matchesFinish;
  });

  const categories = [...new Set(wraps.map(w => w.category).filter(Boolean))];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="wraps-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link 
              to={`/s/${slug}`}
              className="flex items-center gap-2 text-white/60 hover:text-white"
            >
              <ArrowLeft size={18} />
              <span className="font-bold" style={{ color: accentColor }}>{studio.name}</span>
            </Link>
            <Link to={`/s/${slug}/visualize`}>
              <Button 
                className="font-semibold uppercase tracking-wider text-xs px-6"
                style={{ backgroundColor: accentColor, color: '#000' }}
              >
                Visualize
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold mb-4">Wrap Catalog</h1>
            <p className="text-white/60 mb-8">Explore our full collection of wrap colors and finishes</p>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search wraps..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-[#171717] border-white/10 focus:border-[#F5D061] h-12"
                  data-testid="wrap-search"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'gloss', 'satin', 'matte'].map((finish) => (
                  <button
                    key={finish}
                    onClick={() => setFilterFinish(finish)}
                    className={`px-4 py-2 rounded-sm text-sm font-medium capitalize transition-all ${
                      filterFinish === finish 
                        ? 'text-black' 
                        : 'bg-[#171717] text-white/60 hover:bg-[#1f1f1f]'
                    }`}
                    style={filterFinish === finish ? { backgroundColor: accentColor } : {}}
                    data-testid={`filter-${finish}`}
                  >
                    {finish === 'all' ? 'All' : finish}
                  </button>
                ))}
              </div>
            </div>

            {/* Wraps Grid */}
            {filteredWraps.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-white/40">No wraps found matching your criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredWraps.map((wrap) => (
                  <Link
                    key={wrap.id}
                    to={`/s/${slug}/visualize?wrap=${wrap.id}`}
                    className="group relative aspect-square bg-[#0F0F0F] border border-white/5 rounded-sm overflow-hidden card-hover"
                    data-testid={`wrap-card-${wrap.id}`}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ backgroundColor: wrap.color_hex }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform">
                      <p className="font-medium mb-1">{wrap.name}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-white/50 capitalize">{wrap.finish}</p>
                        {wrap.category && (
                          <p className="text-xs text-white/30">{wrap.category}</p>
                        )}
                      </div>
                    </div>
                    {wrap.is_featured && (
                      <div 
                        className="absolute top-2 left-2 text-xs px-2 py-1 rounded text-black font-semibold"
                        style={{ backgroundColor: accentColor }}
                      >
                        Featured
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default StudioWrapsPage;
